import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { PricingTableSkeletonRows } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { TableRow } from '@experiences/components/table/table-row';
import { Typography } from '@usepepper/ui/atoms/typography';
import { VariantPackCostChange } from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { View, ViewStyle } from 'react-native';

/** Hidden column that keeps catalog rows sorted below local rows as a section. */
export const CATALOG_SECTION_COLUMN_ID = '__catalog_section__';

const CATALOG_SKELETON_ROW_COUNT = 3;
const DIVIDER_ROW_KEY = '__catalog_divider__';
const SKELETON_ROW_KEY = '__catalog_skeleton__';

/**
 * `local` rows have no kind. `result` rows are catalog matches rendered like
 * local rows. `divider`/`skeleton` are presentational marker rows. The catalog
 * empty state is not a row — it renders via the Table's `listFooter` so it stays
 * viewport-centered and doesn't scroll horizontally.
 */
export type CatalogRowKind = 'divider' | 'result' | 'skeleton';

export interface CatalogRowMeta {
  catalogCount?: number;
  catalogKind?: CatalogRowKind;
}

type ManagementRow = VariantPackCostChange & CatalogRowMeta;

// A zeroed row so marker rows satisfy the column accessors (which run during
// sorting). Marker rows never render cells — renderRow short-circuits them.
const ZERO_ROW: VariantPackCostChange = {
  costChangeGrossProfitImpactInMicros: 0,
  costDeltaInMicros: 0,
  costInMicros: 0,
  editsGrossProfitImpactInMicros: 0,
  grossMarginPercentage: 0,
  grossMarginPercentageDelta: 0,
  grossProfitDeltaInMicros: 0,
  grossProfitImpactInMicros: 0,
  grossProfitInMicros: 0,
  groups: [],
  invoiceHistory: [],
  isLocked: false,
  netGrossProfitImpactInMicros: 0,
  priceDeltaInMicros: 0,
  priceInMicros: 0,
  quantity: 0,
  restaurantUUID: '',
  supplierUUID: '',
  variantPackId: '',
  variantPackName: '',
  variantPackUOM: '',
  variantPackUUID: '',
  weeklyPurchaseQuantities: [],
};

/**
 * Section rank used by the hidden sort column: local (0) → divider (1) →
 * catalog results/skeletons/empty (2). Returned as a string so it fits the
 * table's `ColumnDef<VariantPackCostChange, string>` column type; lexical order
 * matches numeric order for single digits.
 */
export const getCatalogSectionRank = (row: VariantPackCostChange): string => {
  const kind = (row as ManagementRow).catalogKind;
  if (!kind) {
    return '0';
  }
  return kind === 'divider' ? '1' : '2';
};

export const getCatalogRowKind = (
  row: VariantPackCostChange,
): CatalogRowKind | undefined => (row as ManagementRow).catalogKind;

const makeMarkerRow = (
  variantPackUUID: string,
  kind: CatalogRowKind,
  catalogCount?: number,
): VariantPackCostChange =>
  ({
    ...ZERO_ROW,
    catalogCount,
    catalogKind: kind,
    variantPackUUID,
  }) as ManagementRow;

/** Divider row shown between the local list and the catalog results. */
export const makeCatalogDividerRow = (count: number): VariantPackCostChange =>
  makeMarkerRow(DIVIDER_ROW_KEY, 'divider', count);

export const makeCatalogSkeletonRow = (): VariantPackCostChange =>
  makeMarkerRow(SKELETON_ROW_KEY, 'skeleton');

export const tagCatalogResults = (
  results: VariantPackCostChange[],
): VariantPackCostChange[] =>
  results.map((row) => ({ ...row, catalogKind: 'result' }) as ManagementRow);

interface CatalogDividerRowProps {
  index: number;
  label: string;
}

export const CatalogDividerRow: React.FC<CatalogDividerRowProps> = ({
  index,
  label,
}) => {
  const [styles, theme] = useStyles();
  return (
    <TableRow index={index}>
      <View style={styles.divider}>
        <View style={styles.label}>
          <Typography
            color={theme.sk.color.text.secondary}
            variant="paragraph-sm-600"
          >
            {label}
          </Typography>
        </View>
      </View>
    </TableRow>
  );
};

export const CatalogSkeletonRow: React.FC<{ index: number }> = ({ index }) => {
  const [styles] = useStyles();
  return (
    <TableRow index={index}>
      <View style={styles.skeletonContainer}>
        <PricingTableSkeletonRows rowCount={CATALOG_SKELETON_ROW_COUNT} />
      </View>
    </TableRow>
  );
};

const useStyles = createStyles((theme) => ({
  divider: {
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    flex: 1,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  label: {
    alignSelf: 'flex-start',
    left: 0,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    position: 'sticky' as ViewStyle['position'],
  },
  skeletonContainer: {
    flex: 1,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
